<template>
  <div 
    ref="containerRef"
    class="relative h-12 rounded-full cursor-pointer"
    :style="{ width: `${width}px` }"
    @mousedown="handleMouseDown"
  >
    <!-- CCT Gradient Background -->
    <div 
      class="absolute inset-0 rounded-full overflow-hidden"
      :style="{ background: cctGradient }"
    />
    
    <!-- Pips overlay -->
    <div class="absolute inset-0">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { ctToRGB } from '@/lib/colorConversion'

interface Props {
  width?: number
  minCt?: number  // In mirek (e.g., 153 = 6500K cool)
  maxCt?: number  // In mirek (e.g., 500 = 2000K warm)
}

const props = withDefaults(defineProps<Props>(), {
  width: 300,
  minCt: 153,  // 6500K (cool/daylight)
  maxCt: 500   // 2000K (warm/candlelight)
})

const emit = defineEmits<{
  mouseDown: [event: MouseEvent]
}>()

const containerRef = ref<HTMLDivElement>()

// Generate gradient from cool (left) to warm (right)
const cctGradient = computed(() => {
  const stops: string[] = []
  const numStops = 10
  
  for (let i = 0; i <= numStops; i++) {
    const t = i / numStops
    // Interpolate from minCt (cool/left) to maxCt (warm/right)
    const ct = props.minCt + t * (props.maxCt - props.minCt)
    const rgb = ctToRGB(ct)
    stops.push(`rgb(${rgb.r}, ${rgb.g}, ${rgb.b}) ${t * 100}%`)
  }
  
  return `linear-gradient(to right, ${stops.join(', ')})`
})

const handleMouseDown = (event: MouseEvent) => {
  emit('mouseDown', event)
}

defineExpose({
  containerRef
})
</script>
